'use client';

import * as React from 'react';
import { Building2, ShieldCheck, MapPin, Phone, Briefcase, Mail, ExternalLink } from 'lucide-react';
import Link from 'next/link';
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';
import { allIndustries } from '@/doi-tac-module/lib/industry-data';
import { industryTranslations } from '@/doi-tac-module/constants/industry-translations';
import { AgencyProfile } from '@/doi-tac-module/lib/types';
// Remove AgencyContactButtons as it's no longer used here

interface AgencyAvatarHoverProps {
    agencyId: string;
    agencyDetail: AgencyProfile | null;
    language: string;
    resolvedAgencyName: string;
}

export function AgencyAvatarHover({
    agencyId,
    agencyDetail,
    language,
    resolvedAgencyName
}: AgencyAvatarHoverProps) {
    const t = agencyTranslations as any;

    const getIndustryName = (slug: string | string[]) => {
        if (!slug) return '---';
        const actualSlug = Array.isArray(slug) ? slug[0] : slug;
        if (typeof actualSlug !== 'string') return '---';

        const found = allIndustries.find(i => i.slug?.toLowerCase() === actualSlug.toLowerCase());
        const nameToTranslate = found ? found.name : actualSlug;

        const translation = (industryTranslations as any)[nameToTranslate];
        if (translation) return translation[language as 'vi' | 'bn'] || nameToTranslate;

        return found ? found.name : (actualSlug.charAt(0).toUpperCase() + actualSlug.slice(1).replace(/-/g, ' '));
    };

    const agencyLogo = React.useMemo(() => {
        if (!agencyDetail) return '';
        return typeof agencyDetail.companyLogo === 'string'
            ? agencyDetail.companyLogo
            : (agencyDetail.companyLogo as any)?.url || '';
    }, [agencyDetail]);

    return (
        <div className="flex flex-col bg-white overflow-hidden shadow-2xl rounded-xl border-[#F2B92A]/30">
            {/* Khối 3 ở trên cùng: Thông tin Người đại diện (Trọng tâm) */}
            <div className="p-4 bg-slate-50/80 border-b border-slate-100 flex items-center gap-3">
                <Avatar className="h-11 w-11 border border-white shadow-sm shrink-0">
                    <AvatarImage src={agencyDetail?.avatarUrl || (typeof agencyDetail?.representativePhoto === 'string' ? agencyDetail?.representativePhoto : (agencyDetail?.representativePhoto as any)?.url)} className="object-cover" />
                    <AvatarFallback className="bg-white text-slate-300 text-sm italic font-serif">
                        {(agencyDetail?.managingDirector || agencyDetail?.representativeName || resolvedAgencyName)?.charAt(0) || 'R'}
                    </AvatarFallback>
                </Avatar>
                <div className="flex flex-col text-left">
                    <span className="text-base font-bold text-slate-900 leading-tight">
                        {agencyDetail?.managingDirector || agencyDetail?.representativeName || resolvedAgencyName}
                    </span>
                    <span className="text-[11px] text-slate-500 font-medium mt-0.5">
                        {agencyDetail?.representativeTitle || (agencyDetail?.mainExpertiseI18n as any)?.[language] || agencyDetail?.mainExpertise || t.hover?.representative?.[language]}
                    </span>
                </div>
            </div>

            {/* Khối 1 ở giữa: Logo và tên Công ty (Bổ trợ) */}
            <div className="px-4 py-2 border-b border-slate-50 flex items-center gap-2.5 bg-white">
                <Link href={`/cac-dai-ly/${agencyId}`} className="group flex items-center gap-2.5 w-full">
                    <div className="h-6 w-6 min-w-6 rounded bg-white border border-slate-100 p-0.5 flex items-center justify-center shadow-sm overflow-hidden group-hover:border-primary/50 transition-colors">
                        {agencyLogo ? (
                            <img src={agencyLogo} alt="Logo" className="w-full h-full object-contain" />
                        ) : (
                            <Building2 className="h-3 w-3 text-slate-300" />
                        )}
                    </div>
                    <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-1">
                            <h4 className="text-[11px] font-semibold text-slate-500 leading-tight group-hover:text-primary transition-colors">
                                {resolvedAgencyName}
                            </h4>
                            {agencyDetail?.rlNumber && (
                                <ShieldCheck className="h-3 w-3 text-[#FF6921]" />
                            )}
                        </div>
                        <div className={`mt-0.5 px-1.5 py-0.5 rounded-[3px] text-[9px] font-bold text-white w-fit ${agencyDetail?.role === 'MASTER_CTV' ? 'bg-[#19A6DF]' : 'bg-[#F2B92A]'}`}>
                            {agencyDetail?.role === 'MASTER_CTV'
                                ? t.hover?.master?.[language]
                                : t.hover?.level1?.[language]}
                        </div>
                    </div>
                </Link>
            </div>

            {/* Khối 2 ở dưới cùng: Thông tin chi tiết Công ty (Chi tiết) */}
            <div className="p-4 space-y-3 bg-white">
                {agencyDetail?.rlNumber && (
                    <div className="flex items-start gap-3 p-2 rounded-lg bg-blue-50/30 border border-blue-100/30">
                        <ShieldCheck className="h-3 w-3 text-blue-500 mt-0.5 shrink-0" />
                        <div className="flex flex-col text-left">
                            <span className="text-[9px] text-slate-400 font-medium">{t.hover?.rlNumber?.[language]}</span>
                            <span className="text-[11px] font-mono font-bold text-slate-600">{agencyDetail.rlNumber}</span>
                        </div>
                    </div>
                )}

                <div className="flex items-start gap-3">
                    <MapPin className="h-3 w-3 text-slate-400 mt-0.5 shrink-0" />
                    <div className="flex flex-col text-left">
                        <span className="text-[9px] text-slate-400 font-medium">{t.hover?.address?.[language]}</span>
                        <span className="text-[11px] text-slate-600 leading-snug">{agencyDetail?.officeAddress || (agencyDetail as any)?.location || '---'}</span>
                    </div>
                </div>

                {(agencyDetail?.officialPhone || (agencyDetail as any)?.phone) && (
                    <div className="flex items-center gap-3">
                        <Phone className="h-3 w-3 text-slate-400 shrink-0" />
                        <div className="flex flex-col text-left">
                            <span className="text-[9px] text-slate-400 font-medium">{t.hover?.phone?.[language]}</span>
                            <span className="text-[11px] text-slate-600">{(agencyDetail?.officialPhone || (agencyDetail as any)?.phone)}</span>
                        </div>
                    </div>
                )}

                <div className="flex items-start gap-3">
                    <Briefcase className="h-3 w-3 text-slate-400 mt-0.5 shrink-0" />
                    <div className="flex flex-col text-left">
                        <span className="text-[9px] text-slate-400 font-medium">{t.hover?.industry?.[language]}</span>
                        <span className="text-[11px] text-slate-600 leading-snug">
                            {(agencyDetail?.mainIndustries && (agencyDetail.mainIndustries as string[]).length > 0)
                                ? (agencyDetail.mainIndustries as string[]).map((ind: string) => getIndustryName(ind)).join(', ')
                                : (agencyDetail?.mainExpertiseI18n as any)?.[language] || (agencyDetail?.mainExpertise || '---')}
                        </span>
                    </div>
                </div>

                {(agencyDetail?.officialEmail || agencyDetail?.websiteUrl) && (
                    <div className="pt-1 flex flex-wrap gap-2">
                        {agencyDetail?.officialEmail && (
                            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-50 border border-slate-100 text-[9px] text-slate-500">
                                <Mail className="h-2.5 w-2.5 text-slate-400" />
                                {agencyDetail.officialEmail}
                            </div>
                        )}
                        {agencyDetail?.websiteUrl && (
                            <a
                                href={agencyDetail.websiteUrl.startsWith('http') ? agencyDetail.websiteUrl : `https://${agencyDetail.websiteUrl}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 border border-blue-100 text-[9px] text-blue-600 hover:bg-blue-100 transition-colors"
                            >
                                <ExternalLink className="h-2.5 w-2.5" />
                                {t.hover?.website?.[language]}
                            </a>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
